import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
    Grid,
  Card,
  CardContent,
  LinearProgress,
  Alert,
  Tooltip,
  Divider,
  Stack,
  alpha,
  useTheme,
  CircularProgress,
  InputAdornment,
  Switch,
  Autocomplete,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  Add as AddIcon,
    Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  AttachMoney as MoneyIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Receipt as ReceiptIcon,
  ShoppingCart as POIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Warning as WarningIcon,
  Summarize as TopSheetIcon,
  Settings as GlobalsIcon,
  TableChart as DetailIcon,
  Assessment as ReportsIcon,
  LocalFireDepartment as HotCostsIcon,
  Search as SearchIcon,
  Lock as LockIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as EstimatedIcon
} from '@mui/icons-material';
import BudgetTopSheet from './BudgetTopSheet';
import BudgetGlobals from './BudgetGlobals';
import BudgetReports from './BudgetReports';
import BudgetHotCosts from './BudgetHotCosts';
import {
  getBudget,
  createBudget,
  updateBudget,
  deleteBudget,
  addLineItem,
  updateLineItem,
  deleteLineItem,
  getExpenses,
  createExpense,
  approveExpense,
  rejectExpense,
  getPurchaseOrders,
  createPurchaseOrder,
  approvePurchaseOrder,
  formatCurrency,
  getTemplates,
  applyTemplate
} from '../services/budgetService';
import UploadFileIcon from '@mui/icons-material/UploadFile';

// Tab Panel Component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div 
      role="tabpanel" 
      hidden={value !== index} 
      style={{
        height: value === index ? 'calc(100vh - 380px)' : 'auto',
        overflowY: value === index ? 'auto' : 'hidden'
      }}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

// Budget Summary Cards
function BudgetSummaryCards({ totals, currency }) {
  const theme = useTheme();
  
  const cards = [
    { label: 'Total Budget', value: totals?.grandTotal || 0, color: 'primary' },
    { label: 'Above The Line', value: totals?.atl || 0, color: 'info' },
    { label: 'Below The Line', value: (totals?.btlProduction || 0) + (totals?.btlPost || 0), color: 'secondary' },
    { label: 'Actual Spent', value: totals?.actualSpent || 0, color: totals?.variance < 0 ? 'error' : 'success' },
  ];

  return (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      {cards.map((card, index) => (
        <Grid item xs={12} sm={6} md={3} key={index}>
          <Card sx={{ 
            height: '100%',
            background: alpha(theme.palette[card.color].main, 0.08),
            border: `1px solid ${alpha(theme.palette[card.color].main, 0.2)}`
          }}>
            <CardContent>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                {card.label}
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, color: `${card.color}.main` }}>
                {formatCurrency(card.value, currency)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
}

// Variance Indicator
function VarianceIndicator({ budgeted, actual }) {
  const variance = budgeted - actual;
  const isOver = variance < 0;
  
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {isOver ? (
        <TrendingDownIcon sx={{ fontSize: 16, color: 'error.main' }} />
      ) : (
        <TrendingUpIcon sx={{ fontSize: 16, color: 'success.main' }} />
      )}
      <Typography 
        variant="body2" 
        sx={{ 
          color: isOver ? 'error.main' : 'success.main',
          fontWeight: 600 
        }}
      >
        {formatCurrency(Math.abs(variance))}
      </Typography>
    </Box>
  );
}

// Main Budget Component
function Budget({ projectId, projectName, onBudgetChange }) {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  
  // Data states
  const [budgetData, setBudgetData] = useState(null);
  const [categories, setCategories] = useState({});
  const [subcategories, setSubcategories] = useState({});
  const [expenses, setExpenses] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  
  // Dialog states
  const [lineItemDialog, setLineItemDialog] = useState({ open: false, item: null });
  const [expenseDialog, setExpenseDialog] = useState({ open: false, item: null });
  const [poDialog, setPoDialog] = useState({ open: false, item: null });
  const [createBudgetDialog, setCreateBudgetDialog] = useState(false);
  
  // Expanded categories
  const [expandedCategories, setExpandedCategories] = useState({});
  const [fringePresets, setFringePresets] = useState({});
  const [departments, setDepartments] = useState([]);
  const [positionPresets, setPositionPresets] = useState({});
  const [activeAccount, setActiveAccount] = useState(null); // Movie Magic style - drill down to specific account
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [accountSearchOpen, setAccountSearchOpen] = useState(false); // Ctrl+J account search
  const [payrollImportDialog, setPayrollImportDialog] = useState(false);
  const [importVendor, setImportVendor] = useState('ep');
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState([]);
  const [importUnmatched, setImportUnmatched] = useState(0);
  const [importLoading, setImportLoading] = useState(false);

  const defaultTemplateId = 'feature-film';

  // Fetch budget data
  const fetchBudgetData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getBudget(projectId);
      setBudgetData(data);
      if (data.exists) {
        setBudgetData(data.budget);
        
        // Fetch expenses and POs
        const [expenseData, poData] = await Promise.all([
          getExpenses(projectId),
          getPurchaseOrders(projectId)
        ]);
        setExpenses(expenseData.expenses || []);
        setPurchaseOrders(poData.purchaseOrders || []);
        setDepartments(
          (data.budget?.departments?.length ? data.budget.departments : data.departmentPresets) || []
        );
      } else {
        setBudgetData(null);
        setDepartments(data.departmentPresets || []);
      }
      
      setCategories(data.categories || {});
      setSubcategories(data.subcategories || {});
      setFringePresets(data.fringePresets || {});
      setPositionPresets(data.positionPresets || {});
      if (typeof onBudgetChange === 'function') {
        onBudgetChange();
      }
    } catch (err) {
      console.error('Error fetching budget:', err);
      setError('Failed to load budget data');
    } finally {
      setLoading(false);
    }
  }, [projectId, onBudgetChange]);

  const parseCsvText = (text) => {
    const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
    if (!lines.length) return [];
    const headers = lines[0].split(',').map((h) => h.trim().toLowerCase());
    const idx = (name) => headers.findIndex((h) => h.includes(name));
    const rows = lines.slice(1).map((line) => {
      const cols = line.split(',');
      const get = (match) => {
        const i = idx(match);
        return i >= 0 ? cols[i]?.trim() : '';
      };
      const regHours = parseFloat(get('reg')) || 0;
      const otHours = parseFloat(get('ot')) || 0;
      const dtHours = parseFloat(get('dt')) || 0;
      const gross = parseFloat(get('gross')) || parseFloat(get('total')) || 0;
      const fringes = parseFloat(get('fringe')) || 0;
      const taxes = parseFloat(get('tax')) || 0;
      return {
        employee: get('name') || get('employee'),
        position: get('position') || get('title') || get('role') || get('description'),
        department: get('dept'),
        accountCode: get('account') || get('gl') || get('code'),
        description: get('description') || get('position'),
        date: get('date') || get('work'),
        regHours,
        otHours,
        dtHours,
        gross,
        fringes,
        taxes,
        total: gross + fringes + taxes,
      };
    });
    return rows.filter((r) => Object.values(r).some((v) => v));
  };

  const handleImportFile = async (file) => {
    if (!file) return;
    setImportFile(file);
    setImportLoading(true);
    try {
      const text = await file.text();
      const rows = parseCsvText(text);
      setImportPreview(rows);
      setImportUnmatched(0);
    } catch (e) {
      setError(`Failed to parse file: ${e.message}`);
    } finally {
      setImportLoading(false);
    }
  };

  const submitPayrollImport = async () => {
    if (!importPreview.length) {
      setError('No rows to import');
      return;
    }
    setImportLoading(true);
    try {
      const body = {
        vendor: importVendor,
        rows: importPreview,
        fileName: importFile?.name || '',
      };
      const res = await fetch(`/api/budget/${projectId}/payroll-import?vendor=${importVendor}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || 'Import failed');
      setSuccess(`Imported ${data.appliedTotal?.toFixed?.(2) || data.appliedTotal || 0}; unmatched rows: ${data.unmatchedRows}`);
      setImportUnmatched(data.unmatchedRows || 0);
      setPayrollImportDialog(false);
      setImportPreview([]);
      setImportFile(null);
      fetchBudgetData();
    } catch (e) {
      setError(e.message || 'Import failed');
    } finally {
      setImportLoading(false);
    }
  };

  // Helpers for duplicate suffixing (auto-increment -2, -3, ...)
  const escapeRegex = (str = '') => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const getNextDuplicateAccountCode = (baseCode, existingItems = []) => {
    if (!baseCode) return '';
    const escaped = escapeRegex(baseCode);
    const regex = new RegExp(`^${escaped}-(\\d+)$`);
    let maxSuffix = 1;
    existingItems.forEach((li) => {
      const code = li.accountCode || '';
      if (code === baseCode) {
        maxSuffix = Math.max(maxSuffix, 1);
      }
      const match = code.match(regex);
      if (match && match[1]) {
        const num = Number(match[1]);
        if (!Number.isNaN(num)) {
          maxSuffix = Math.max(maxSuffix, num);
        }
      }
    });
    return `${baseCode}-${maxSuffix + 1}`;
  };

  // Duplicate line item (Movie Magic-style copy detail line)
  const handleDuplicateLineItem = async (lineItem) => {
    if (!lineItem) return;
    const baseCode = lineItem.accountCode || '';
    const nextCode = getNextDuplicateAccountCode(baseCode, budgetData?.lineItems || []);
    const duplicatePayload = {
      accountCode: baseCode ? nextCode : '',
      description: lineItem.description || '',
      subcategory: lineItem.subcategory || '',
      position: lineItem.position || '',
      units: lineItem.units || 0,
      unitType: lineItem.unitType || 'flat',
      rate: lineItem.rate || 0,
      multiplier: lineItem.multiplier || 1,
      notes: lineItem.notes || '',
      fringeSettings: lineItem.fringeSettings || defaultFringeSettings,
      etc: lineItem.etc ?? null,
      status: lineItem.status || 'estimated',
      varianceNotes: lineItem.varianceNotes || '',
      actualAmount: lineItem.actualAmount || 0,
      committedAmount: lineItem.committedAmount || 0
    };
    try {
      await addLineItem(projectId, budgetData._id, duplicatePayload);
      await fetchBudgetData();
    } catch (err) {
      setError('Failed to duplicate line item');
    }
  };

  useEffect(() => {
    if (projectId) {
      fetchBudgetData();
    }
  }, [projectId, fetchBudgetData]);

  useEffect(() => {
    const loadTemplates = async () => {
      try {
        setTemplatesLoading(true);
        const data = await getTemplates();
        setTemplates(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Failed to load budget templates:', err);
      } finally {
        setTemplatesLoading(false);
      }
    };

    loadTemplates();
  }, [fetchBudgetData]);

  // Movie Magic style - Ctrl+J keyboard shortcut for instant account search
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'j') {
        e.preventDefault();
        setAccountSearchOpen(true);
      }
      // Escape to close search
      if (e.key === 'Escape' && accountSearchOpen) {
        setAccountSearchOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [accountSearchOpen]);

  // Create new budget
  const handleCreateBudget = async (budgetInfo) => {
    try {
      // Use project name as budget name if not provided
      const { templateId, ...rest } = budgetInfo || {};
      const budgetData = {
        ...rest,
        name: rest.name || projectName || 'Main Budget'
      };

      const newBudget = await createBudget(projectId, budgetData);

      if (templateId) {
        await applyTemplate(projectId, templateId);
        await fetchBudgetData();
      } else {
        setBudgetData(newBudget);
        if (typeof onBudgetChange === 'function') {
          onBudgetChange();
        }
      }

      setCreateBudgetDialog(false);
    } catch (err) {
      setError('Failed to create budget');
    }
  };

  // Delete budget
  const handleDeleteBudget = async () => {
    if (!window.confirm('Delete this budget? This will also delete all expenses and purchase orders. This action cannot be undone.')) return;
    try {
      await deleteBudget(projectId, budgetData._id);
      setBudgetData(null);
      setExpenses([]);
      setPurchaseOrders([]);
    } catch (err) {
      setError('Failed to delete budget');
    }
  };

  // Add/Update line item - Movie Magic flat style (no sub-items)
  const handleSaveLineItem = async (lineItem) => {
    try {
      if (lineItem._id) {
        // Update existing line item
        await updateLineItem(projectId, budgetData._id, lineItem._id, lineItem);
      } else {
        // Add new line item
        await addLineItem(projectId, budgetData._id, lineItem);
      }
      await fetchBudgetData();
      setLineItemDialog({ open: false, item: null });
    } catch (err) {
      setError('Failed to save line item');
    }
  };

  // Delete line item
  const handleDeleteLineItem = async (lineItemId) => {
    if (!window.confirm('Delete this line item?')) return;
    try {
      await deleteLineItem(projectId, budgetData._id, lineItemId);
      await fetchBudgetData();
    } catch (err) {
      setError('Failed to delete line item');
    }
  };

  // Add expense
  const handleSaveExpense = async (expense) => {
    try {
      await createExpense(projectId, expense);
      await fetchBudgetData();
      setExpenseDialog({ open: false, item: null });
    } catch (err) {
      setError('Failed to save expense');
    }
  };

  // Approve/Reject expense
  const handleApproveExpense = async (expenseId) => {
    try {
      await approveExpense(projectId, expenseId);
      await fetchBudgetData();
    } catch (err) {
      setError('Failed to approve expense');
    }
  };

  const handleRejectExpense = async (expenseId) => {
    const reason = window.prompt('Rejection reason:');
    if (!reason) return;
    try {
      await rejectExpense(projectId, expenseId, reason);
      await fetchBudgetData();
    } catch (err) {
      setError('Failed to reject expense');
    }
  };

  // Create PO
  const handleSavePO = async (po) => {
    try {
      await createPurchaseOrder(projectId, po);
      await fetchBudgetData();
      setPoDialog({ open: false, item: null });
    } catch (err) {
      setError('Failed to create purchase order');
    }
  };

  // Approve PO
  const handleApprovePO = async (poId) => {
    try {
      await approvePurchaseOrder(projectId, poId);
      await fetchBudgetData();
    } catch (err) {
      setError('Failed to approve PO');
    }
  };

  // Toggle category expansion
  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  // Movie Magic style - flat list of all line items grouped by category
  const lineItemsByCategory = useMemo(() => {
    if (!budgetData?.lineItems) return {};
    const grouped = {};
    budgetData.lineItems.forEach(item => {
      const subcatInfo = subcategories[item.subcategory];
      const category = subcatInfo?.category || 'OTHER';
      if (!grouped[category]) grouped[category] = [];
      grouped[category].push(item);
    });
    return grouped;
  }, [budgetData?.lineItems, subcategories]);

  // Movie Magic style - filter by specific account (e.g., "3200")
  const filteredLineItemsByAccount = useMemo(() => {
    if (!activeAccount) return null;
    
    const filtered = {};
    (budgetData?.lineItems || []).forEach((item) => {
      const subcatInfo = subcategories[item.subcategory];
      if (!subcatInfo) return;
      
      // Match by account number (e.g., "3200" matches "3200-01", "3200-02", etc.)
      if (subcatInfo.account === activeAccount.account) {
        const category = subcatInfo.category || 'OTHER';
        if (!filtered[category]) filtered[category] = [];
        filtered[category].push(item);
      }
    });
    return filtered;
  }, [activeAccount, budgetData?.lineItems, subcategories]);

  // Movie Magic style - filter by account when active
  const filteredLineItemsByCategory = useMemo(() => {
    if (activeAccount && filteredLineItemsByAccount) {
      return filteredLineItemsByAccount;
    }
    return lineItemsByCategory;
  }, [activeAccount, filteredLineItemsByAccount, lineItemsByCategory]);

  // Loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  // No budget exists - show create option
  if (!budgetData) {
    return (
      <Box sx={{ p: 3 }}>
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 1, border: 1, borderColor: 'divider' }}>
          <MoneyIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
            No Budget Created
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Create a production budget to track costs and expenses
          </Typography>
          <Button 
            variant="contained" 
            startIcon={<AddIcon />}
            onClick={() => setCreateBudgetDialog(true)}
          >
            Create Budget
          </Button>
        </Paper>

        {/* Create Budget Dialog */}
        <CreateBudgetDialog
          open={createBudgetDialog}
          onClose={() => setCreateBudgetDialog(false)}
          onSave={handleCreateBudget}
          defaultName={projectName}
          templates={templates}
          templatesLoading={templatesLoading}
          defaultTemplateId={defaultTemplateId}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {budgetData.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Version {budgetData.version} • {budgetData.status}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchBudgetData}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete Budget">
            <IconButton onClick={handleDeleteBudget} color="error">
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Summary Cards */}
      <BudgetSummaryCards totals={budgetData.totals} currency={budgetData.currency} />

      {/* Budget Progress */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 1, border: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Budget Utilization
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {formatCurrency(budgetData.totals?.actualSpent)} of {formatCurrency(budgetData.totals?.grandTotal)}
          </Typography>
        </Box>
        <LinearProgress 
          variant="determinate" 
          value={Math.min(100, ((budgetData.totals?.actualSpent || 0) / (budgetData.totals?.grandTotal || 1)) * 100)}
          sx={{ 
            height: 10, 
            borderRadius: 1,
            backgroundColor: alpha(theme.palette.primary.main, 0.1),
            '& .MuiLinearProgress-bar': {
              backgroundColor: budgetData.totals?.variance < 0 ? 'error.main' : 'success.main'
            }
          }}
        />
        {budgetData.totals?.variance < 0 && (
          <Alert severity="warning" sx={{ mt: 2 }} icon={<WarningIcon />}>
            Over budget by {formatCurrency(Math.abs(budgetData.totals.variance))}
          </Alert>
        )}
      </Paper>

      {/* Tabs */}
      <Paper sx={{ borderRadius: 1, border: 1, borderColor: 'divider' }}>
        <Tabs 
          value={tabValue} 
          onChange={(e, v) => setTabValue(v)}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Top Sheet" icon={<TopSheetIcon />} iconPosition="start" />
          <Tab label="Detail" icon={<DetailIcon />} iconPosition="start" />
          <Tab label="Hot Costs" icon={<HotCostsIcon />} iconPosition="start" />
          <Tab label="Globals" icon={<GlobalsIcon />} iconPosition="start" />
          <Tab label="Expenses" icon={<ReceiptIcon />} iconPosition="start" />
          <Tab label="Purchase Orders" icon={<POIcon />} iconPosition="start" />
          <Tab label="Reports" icon={<ReportsIcon />} iconPosition="start" />
        </Tabs>

        {/* Top Sheet Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ px: 2 }}>
            <BudgetTopSheet 
              budget={budgetData} 
              subcategories={subcategories} 
              departments={departments}
              onAccountSelect={(accountInfo) => {
                setActiveAccount(accountInfo);
                setTabValue(1);
              }}
            />
          </Box>
        </TabPanel>

        {/* Detail/Budget Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ px: 2 }}>
            {/* Breadcrumb Navigation - minimal */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: 14 }}>
                <Button
                  onClick={() => setActiveAccount(null)}
                  color="primary"
                  size="small"
                  variant="text"
                  sx={{ fontWeight: 700, textTransform: 'none', px: 0.5 }}
                >
                  Budget
                </Button>
                {activeAccount && (
                  <>
                    <Typography variant="body2" color="text.disabled">/</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {categories[activeAccount.category] || activeAccount.category}
                    </Typography>
                    <Typography variant="body2" color="text.disabled">/</Typography>
                    <Button
                      onClick={() => setActiveAccount(null)}
                      size="small"
                      color="primary"
                      variant="text"
                      sx={{ fontFamily: 'monospace', textTransform: 'none', px: 0.5 }}
                    >
                      {activeAccount.account} {activeAccount.label}
                    </Button>
                  </>
                )}
              </Box>
              <Button 
                variant="contained" 
                startIcon={<AddIcon />}
                onClick={() => setLineItemDialog({ open: true, item: null })}
              >
                Add Line Item
              </Button>
            </Box>

            {/* Budget Categories */}
            {Object.entries(categories).map(([catKey, catLabel]) => {
              // Movie Magic style - use filtered items when account is active
              const items = (activeAccount ? filteredLineItemsByCategory : lineItemsByCategory)[catKey] || [];
              const catTotal = items.reduce((sum, item) => sum + (item.budgetAmount || 0), 0);
              const catActual = items.reduce((sum, item) => sum + (item.actualAmount || 0), 0);
              const isExpanded = expandedCategories[catKey] !== false;

              return (
                <Paper 
                  key={catKey} 
                  sx={{ mb: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}
                >
                  <Box 
                    sx={{ 
                      p: 2, 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'action.hover' }
                    }}
                    onClick={() => toggleCategory(catKey)}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {isExpanded ? <CollapseIcon /> : <ExpandIcon />}
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {catLabel}
                      </Typography>
                      <Chip label={`${items.length} items`} size="small" />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="body2" color="text.secondary">Budget</Typography>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {formatCurrency(catTotal)}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="body2" color="text.secondary">Actual</Typography>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {formatCurrency(catActual)}
                        </Typography>
                      </Box>
                      <VarianceIndicator budgeted={catTotal} actual={catActual} />
                    </Box>
                  </Box>

                  {isExpanded && items.length > 0 && (
                    <TableContainer>
                      <Table
                        size="small"
                        sx={{
                          tableLayout: 'fixed',
                          '& td, & th': { overflow: 'hidden', textOverflow: 'ellipsis' }
                        }}
                      >
                        <colgroup>
                          <col style={{ width: '12%' }} />   {/* Account */}
                          <col style={{ width: '28%' }} />   {/* Description */}
                          <col style={{ width: '8%' }} />    {/* Units */}
                          <col style={{ width: '8%' }} />    {/* Rate */}
                          <col style={{ width: '10%' }} />   {/* Budget */}
                          <col style={{ width: '10%' }} />   {/* Committed */}
                          <col style={{ width: '10%' }} />   {/* Actual */}
                          <col style={{ width: '7%' }} />    {/* ETC */}
                          <col style={{ width: '7%' }} />    {/* EAC */}
                          <col style={{ width: '5%' }} />    {/* Variance */}
                          <col style={{ width: '5%' }} />    {/* Actions */}
                        </colgroup>
                        <TableHead>
                          <TableRow>
                            <TableCell>Account</TableCell>
                            <TableCell>Description</TableCell>
                            <TableCell align="right">Units</TableCell>
                            <TableCell align="right">Rate</TableCell>
                            <TableCell align="right">Budget</TableCell>
                            <TableCell align="right">Committed</TableCell>
                            <TableCell align="right">Actual</TableCell>
                            <TableCell align="right">ETC</TableCell>
                            <TableCell align="right">EAC</TableCell>
                            <TableCell align="right">Variance</TableCell>
                            <TableCell align="center">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {/* Movie Magic Style - Flat detail lines, no nesting */}
                          {items.map((item) => {
                            // Simple flat calculation - no child items
                            const budget = item.budgetAmount || 0;
                            const actual = item.actualAmount || 0;
                            const committed = item.committedAmount || 0;
                            
                            // ETC: Use override if set, otherwise calculate (budget - actual - committed)
                            const calculatedETC = Math.max(0, budget - actual - committed);
                            const etc = item.etc !== null && item.etc !== undefined ? item.etc : calculatedETC;
                            
                            // EAC: Use override if set, otherwise calculate (actual + committed + ETC)
                            const calculatedEAC = actual + committed + etc;
                            const eac = item.eac !== null && item.eac !== undefined ? item.eac : calculatedEAC;
                            
                            return (
                              <TableRow 
                                key={item._id}
                                hover
                                sx={{ 
                                  cursor: 'pointer',
                                  bgcolor: item.status === 'locked' ? alpha(theme.palette.warning.main, 0.05) : 'inherit',
                                  opacity: item.status === 'locked' ? 0.85 : 1
                                }}
                                onClick={() => {
                                  setLineItemDialog({ open: true, item });
                                }}
                              >
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Typography sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
                                      {item.accountCode}
                                    </Typography>
                                    {item.status === 'locked' && (
                                      <Tooltip title="Locked">
                                        <LockIcon sx={{ fontSize: 14, color: 'warning.main' }} />
                                      </Tooltip>
                                    )}
                                    {item.status === 'confirmed' && (
                                      <Tooltip title="Confirmed">
                                        <CheckCircleIcon sx={{ fontSize: 14, color: 'success.main' }} />
                                      </Tooltip>
                                    )}
                                  </Box>
                                </TableCell>
                                <TableCell sx={{ whiteSpace: 'normal' }}>
                                  {item.description}
                                </TableCell>
                                <TableCell align="right">{item.units} {item.unitType}</TableCell>
                                <TableCell align="right">{formatCurrency(item.rate)}</TableCell>
                                <TableCell align="right">{formatCurrency(budget)}</TableCell>
                                <TableCell align="right" sx={{ color: committed > 0 ? 'warning.main' : 'text.secondary' }}>
                                  {formatCurrency(committed)}
                                </TableCell>
                                <TableCell align="right">{formatCurrency(actual)}</TableCell>
                                <TableCell align="right" sx={{ color: 'info.main' }}>{formatCurrency(etc)}</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600 }}>{formatCurrency(eac)}</TableCell>
                                <TableCell align="right">
                                  <Tooltip 
                                    title={item.varianceNotes || ''} 
                                    arrow 
                                    placement="left"
                                    disableHoverListener={!item.varianceNotes}
                                  >
                                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                      <VarianceIndicator budgeted={budget} actual={eac} />
                                      {item.varianceNotes && (
                                        <WarningIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                                      )}
                                    </Box>
                                  </Tooltip>
                                </TableCell>
                                <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                    <IconButton 
                                      size="small" 
                                      color="primary"
                                      onClick={() => handleDuplicateLineItem(item)}
                                    >
                                      <CopyIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton 
                                      size="small" 
                                      color="error"
                                      onClick={() => handleDeleteLineItem(item._id)}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Paper>
              );
            })}
          </Box>
        </TabPanel>

        {/* Hot Costs Tab */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ px: 2 }}>
            <BudgetHotCosts budget={budgetData} expenses={expenses} subcategories={subcategories} />
          </Box>
        </TabPanel>

        {/* Globals Tab */}
        <TabPanel value={tabValue} index={3}>
          <Box sx={{ px: 2 }}>
            <BudgetGlobals
              budget={budgetData}
              onSave={async (data) => {
                try {
                  await updateBudget(projectId, budgetData._id, data);
                  await fetchBudgetData();
                  setSuccess('Globals saved successfully');
                } catch (err) {
                  setError('Failed to save globals');
                }
              }}
            />
          </Box>
        </TabPanel>

        {/* Expenses Tab */}
        <TabPanel value={tabValue} index={4}>
          <Box sx={{ px: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
              <Button 
                variant="contained" 
                startIcon={<AddIcon />}
                onClick={() => setExpenseDialog({ open: true, item: null })}
              >
                Log Expense
              </Button>
            </Box>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {expenses.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">No expenses logged yet</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    expenses.map((expense) => (
                      <TableRow key={expense._id} hover>
                        <TableCell>
                          {new Date(expense.expenseDate).toLocaleDateString()}
                        </TableCell>
                        <TableCell>{expense.description}</TableCell>
                        <TableCell>
                          <Chip 
                            label={subcategories[expense.subcategory]?.label || expense.subcategory} 
                            size="small" 
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          {formatCurrency(expense.amount)}
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={expense.status}
                            size="small"
                            color={
                              expense.status === 'approved' ? 'success' :
                              expense.status === 'rejected' ? 'error' :
                              expense.status === 'paid' ? 'info' : 'default'
                            }
                          />
                        </TableCell>
                        <TableCell align="center">
                          {expense.status === 'pending' && (
                            <>
                              <Tooltip title="Approve">
                                <IconButton 
                                  size="small" 
                                  color="success"
                                  onClick={() => handleApproveExpense(expense._id)}
                                >
                                  <ApproveIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Reject">
                                <IconButton 
                                  size="small" 
                                  color="error"
                                  onClick={() => handleRejectExpense(expense._id)}
                                >
                                  <RejectIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </TabPanel>

        {/* Purchase Orders Tab */}
        <TabPanel value={tabValue} index={5}>
          <Box sx={{ px: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
              <Button 
                variant="contained" 
                startIcon={<AddIcon />}
                onClick={() => setPoDialog({ open: true, item: null })}
              >
                Create PO
              </Button>
            </Box>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>PO #</TableCell>
                    <TableCell>Vendor</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {purchaseOrders.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">No purchase orders yet</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    purchaseOrders.map((po) => (
                      <TableRow key={po._id} hover>
                        <TableCell sx={{ fontWeight: 600 }}>{po.poNumber}</TableCell>
                        <TableCell>{po.vendor?.name}</TableCell>
                        <TableCell>
                          {new Date(po.issueDate).toLocaleDateString()}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          {formatCurrency(po.total)}
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={po.status}
                            size="small"
                            color={
                              po.status === 'approved' ? 'success' :
                              po.status === 'cancelled' ? 'error' :
                              po.status === 'completed' ? 'info' : 'warning'
                            }
                          />
                        </TableCell>
                        <TableCell align="center">
                          {po.status === 'pending-approval' && (
                            <Tooltip title="Approve">
                              <IconButton 
                                size="small" 
                                color="success"
                                onClick={() => handleApprovePO(po._id)}
                              >
                                <ApproveIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </TabPanel>

        {/* Reports Tab */}
        <TabPanel value={tabValue} index={6}>
          <Box sx={{ px: 2 }}>
            <BudgetReports 
              projectId={projectId} 
              budget={budgetData}
              onBudgetUpdate={fetchBudgetData}
              onOpenPayrollImport={() => setPayrollImportDialog(true)}
            />
          </Box>
        </TabPanel>
      </Paper>

      {/* Dialogs */}
      <LineItemDialog
        open={lineItemDialog.open}
        item={lineItemDialog.item}
        subcategories={subcategories}
        fringePresets={fringePresets}
        positionPresets={positionPresets}
        lineItems={budgetData?.lineItems || []}
        onClose={() => setLineItemDialog({ open: false, item: null })}
        onSave={handleSaveLineItem}
        onDeleteLineItem={handleDeleteLineItem}
        onRefresh={fetchBudgetData}
        onLogExpense={(lineItem) => {
          setLineItemDialog({ open: false, item: null });
          setExpenseDialog({ open: true, item: lineItem });
        }}
      />

      <ExpenseDialog
        open={expenseDialog.open}
        subcategories={subcategories}
        lineItems={budgetData?.lineItems || []}
        preSelectedLineItem={expenseDialog.item}
        onClose={() => setExpenseDialog({ open: false, item: null })}
        onSave={handleSaveExpense}
      />

      <PurchaseOrderDialog
        open={poDialog.open}
        subcategories={subcategories}
        lineItems={budgetData?.lineItems || []}
        onClose={() => setPoDialog({ open: false, item: null })}
        onSave={handleSavePO}
      />

      <CreateBudgetDialog
        open={createBudgetDialog}
        onClose={() => setCreateBudgetDialog(false)}
        onSave={handleCreateBudget}
        defaultName={projectName}
        templates={templates}
        templatesLoading={templatesLoading}
        defaultTemplateId={defaultTemplateId}
      />

      {/* Movie Magic Style - Ctrl+J Account Search Dialog */}
      <Dialog 
        open={accountSearchOpen} 
        onClose={() => setAccountSearchOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { position: 'absolute', top: '20%' } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SearchIcon />
            <Typography variant="h6">Jump to Account</Typography>
            <Chip label="Ctrl+J" size="small" variant="outlined" sx={{ ml: 'auto' }} />
          </Box>
        </DialogTitle>
        <DialogContent>
          <Autocomplete
            autoFocus
            openOnFocus
            options={Object.entries(subcategories).map(([key, info]) => ({
              key,
              account: info.account,
              label: info.label,
              category: info.category
            }))}
            getOptionLabel={(option) => `${option.account} - ${option.label}`}
            groupBy={(option) => categories[option.category] || option.category}
            renderInput={(params) => (
              <TextField 
                {...params} 
                placeholder="Type account number or name..."
                autoFocus
                sx={{ mt: 1 }}
              />
            )}
            renderOption={(props, option) => (
              <li {...props}>
                <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
                  <Typography sx={{ fontFamily: 'monospace', fontWeight: 600, minWidth: 50 }}>
                    {option.account}
                  </Typography>
                  <Typography>{option.label}</Typography>
                </Box>
              </li>
            )}
            onChange={(e, value) => {
              if (value) {
                setActiveAccount({
                  account: value.account,
                  label: value.label,
                  category: value.category,
                  subcategory: value.key
                });
                setTabValue(1);
                setAccountSearchOpen(false);
              }
            }}
            sx={{ minWidth: 300 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
            Start typing to filter accounts. Press Enter to jump to the selected account.
          </Typography>
        </DialogContent>
      </Dialog>

      {/* Payroll Import Dialog */}
      <Dialog
        open={payrollImportDialog}
        onClose={() => setPayrollImportDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Import Payroll Actuals</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              label="Payroll Vendor"
              value={importVendor}
              onChange={(e) => setImportVendor(e.target.value)}
              fullWidth
              size="small"
            >
              <MenuItem value="ep">Entertainment Partners (EP)</MenuItem>
              <MenuItem value="greenslate">Greenslate</MenuItem>
              <MenuItem value="wrapbook">Wrapbook</MenuItem>
              <MenuItem value="cast-crew">Cast & Crew</MenuItem>
              <MenuItem value="generic">Generic CSV</MenuItem>
            </TextField>

            <Alert severity="info">
              Upload a CSV export from your payroll vendor. The system will match rows to budget line items by account code.
            </Alert>

            <Button
              variant="outlined"
              component="label"
              startIcon={<UploadFileIcon />}
              fullWidth
            >
              {importFile ? importFile.name : 'Select CSV File'}
              <input
                type="file"
                accept=".csv"
                hidden
                onChange={(e) => handleImportFile(e.target.files[0])}
              />
            </Button>

            {importLoading && <CircularProgress size={24} sx={{ alignSelf: 'center' }} />}

            {importPreview.length > 0 && (
              <Paper variant="outlined" sx={{ p: 2, maxHeight: 300, overflow: 'auto' }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Preview ({importPreview.length} rows)
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Employee</TableCell>
                      <TableCell>Position</TableCell>
                      <TableCell>Account</TableCell>
                      <TableCell align="right">Gross</TableCell>
                      <TableCell align="right">Fringes</TableCell>
                      <TableCell align="right">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {importPreview.slice(0, 10).map((row, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{row.employee}</TableCell>
                        <TableCell>{row.position}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{row.accountCode}</TableCell>
                        <TableCell align="right">{formatCurrency(row.gross)}</TableCell>
                        <TableCell align="right">{formatCurrency(row.fringes)}</TableCell>
                        <TableCell align="right">{formatCurrency(row.total)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {importPreview.length > 10 && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    ...and {importPreview.length - 10} more rows
                  </Typography>
                )}
              </Paper>
            )}

            {importUnmatched > 0 && (
              <Alert severity="warning">
                {importUnmatched} rows could not be matched to budget line items.
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPayrollImportDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={submitPayrollImport}
            disabled={importLoading || importPreview.length === 0}
          >
            Import Actuals
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// Create Budget Dialog
function CreateBudgetDialog({
  open,
  onClose,
  onSave,
  defaultName,
  templates = [],
  templatesLoading = false,
  defaultTemplateId
}) {
  const [name, setName] = useState(defaultName || 'Main Budget');
  const [currency, setCurrency] = useState('USD');
  const [contingency, setContingency] = useState(10);
  const [templateId, setTemplateId] = useState('');

  const hasTemplates = Array.isArray(templates) && templates.length > 0;

  // Reset name whenever dialog opens so it reflects the latest project title
  React.useEffect(() => {
    if (!open) return;
    setName(defaultName || 'Main Budget');
  }, [defaultName, open]);

  // Reset template selection whenever dialog opens or templates change
  React.useEffect(() => {
    if (!open) return;
    const defaultExists = templates?.some((tpl) => tpl.id === defaultTemplateId);
    setTemplateId(defaultExists ? defaultTemplateId : '');
  }, [open, templates, defaultTemplateId]);

  const handleSave = () => {
    onSave({
      name,
      currency,
      contingencyPercent: contingency,
      templateId: templateId || null
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create Production Budget</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <TextField
            label="Budget Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
          />
          <TextField
            select
            label="Template"
            value={templateId || ''}
            onChange={(e) => setTemplateId(e.target.value)}
            fullWidth
            disabled={!hasTemplates || templatesLoading}
            helperText={
              templatesLoading
                ? 'Loading templates...'
                : 'Start from the feature film template or choose another one.'
            }
          >
            <MenuItem value="">
              Start from blank budget
            </MenuItem>
            {templates.map((template) => (
              <MenuItem key={template.id} value={template.id}>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {template.name}
                  </Typography>
                  {template.description && (
                    <Typography variant="caption" color="text.secondary">
                      {template.description}
                    </Typography>
                  )}
                </Box>
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Currency"
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            fullWidth
          >
            <MenuItem value="USD">USD - US Dollar</MenuItem>
            <MenuItem value="EUR">EUR - Euro</MenuItem>
            <MenuItem value="GBP">GBP - British Pound</MenuItem>
            <MenuItem value="CAD">CAD - Canadian Dollar</MenuItem>
          </TextField>
          <TextField
            label="Contingency %"
            type="number"
            value={contingency}
            onChange={(e) => setContingency(Number(e.target.value))}
            fullWidth
            helperText="Standard is 10% for most productions"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave}>Create Budget</Button>
      </DialogActions>
    </Dialog>
  );
}

// Line Item Dialog
const CATEGORY_LABELS = {
  ATL: 'Above The Line',
  BTL_PRODUCTION: 'Below The Line - Production',
  BTL_POST: 'Below The Line - Post',
  OTHER: 'Other Costs'
};

const defaultFringeSettings = {
  applyFringes: true,
  groups: [
    {
      presetKey: 'default',
      rateOverride: '',
      applyToBase: true,
      applyToOT: true,
      capAmount: ''
    }
  ],
  notes: ''
};

function LineItemDialog({ open, item, subcategories, fringePresets = {}, positionPresets = {}, lineItems = [], onClose, onSave, onDeleteLineItem, onRefresh, onLogExpense }) {
  const theme = useTheme();
  const [formData, setFormData] = useState({
    accountCode: '',
    description: '',
    subcategory: '',
    position: '',
    units: 1,
    unitType: 'flat',
    rate: 0,
    multiplier: 1,
    notes: '',
    fringeSettings: defaultFringeSettings,
    // Movie Magic style - ETC override and status
    etcOverride: '',
    status: 'estimated',
    varianceNotes: ''
  });
  const [fieldErrors, setFieldErrors] = useState({});

  // Get available positions for the selected subcategory
  const availablePositions = useMemo(() => {
    if (!formData.subcategory || !positionPresets[formData.subcategory]) {
      return [];
    }
    return positionPresets[formData.subcategory];
  }, [formData.subcategory, positionPresets]);

  // Account options for Add mode helper
  const accountOptions = useMemo(() => {
    const categories = {};
    Object.entries(subcategories).forEach(([key, info]) => {
      if (!categories[info.category]) {
        categories[info.category] = [];
      }
      categories[info.category].push({
        key,
        account: info.account,
        label: info.label,
        category: info.category,
        categoryLabel: CATEGORY_LABELS[info.category] || info.category
      });
    });
    return Object.values(categories).flat();
  }, [subcategories]);

  const handleAccountHelperSelect = (option) => {
    handleSubcategoryChange(option.key);
  };

  useEffect(() => {
    if (item) {
      setFormData({
        accountCode: item.accountCode || '',
        description: item.description || '',
        subcategory: item.subcategory || '',
        position: item.position || '',
        units: item.units || 1,
        unitType: item.unitType || 'flat',
        rate: item.rate || 0,
        multiplier: item.multiplier || 1,
        notes: item.notes || '',
        fringeSettings: {
          applyFringes: item.fringeSettings?.applyFringes !== false,
          groups: item.fringeSettings?.groups && Array.isArray(item.fringeSettings.groups) && item.fringeSettings.groups.length
            ? item.fringeSettings.groups
            : [{
                presetKey: item.fringeSettings?.preset || 'default',
                rateOverride: item.fringeSettings?.rateOverride ?? '',
                applyToBase: true,
                applyToOT: true,
                capAmount: ''
              }],
          notes: item.fringeSettings?.notes || ''
        },
        // Movie Magic style fields
        etcOverride: item.etc ?? '',
        status: item.status || 'estimated',
        varianceNotes: item.varianceNotes || ''
      });
    } else {
      setFormData({
        accountCode: '',
        description: '',
        subcategory: '',
        position: '',
        units: 1,
        unitType: 'flat',
        rate: 0,
        multiplier: 1,
        notes: '',
        fringeSettings: defaultFringeSettings,
        etcOverride: '',
        status: 'estimated',
        varianceNotes: ''
      });
      setFieldErrors({});
    }
    }, [item, open]);

  const handleChange = (field) => (e) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleFringeChange = (field) => (e, value) => {
    const inputValue = e?.target ? e.target.type === 'checkbox' ? e.target.checked : e.target.value : value;
    setFormData(prev => ({
      ...prev,
      fringeSettings: {
        ...prev.fringeSettings,
        [field]: inputValue
      }
    }));
  };

  // Quick overtime multiplier presets (LineItemDialog scope)
  const applyMultiplierPreset = (mult) => {
    setFormData(prev => ({ ...prev, multiplier: mult }));
  };

  const handleFringeGroupChange = (index, field) => (e) => {
    const value = e?.target ? e.target.value : e;
    setFormData(prev => {
      const groups = [...(prev.fringeSettings.groups || [])];
      groups[index] = { ...groups[index], [field]: value };
      return { ...prev, fringeSettings: { ...prev.fringeSettings, groups } };
    });
  };

  const handleFringeGroupToggle = (index, field) => (e) => {
    const checked = e.target.checked;
    setFormData(prev => {
      const groups = [...(prev.fringeSettings.groups || [])];
      groups[index] = { ...groups[index], [field]: checked };
      return { ...prev, fringeSettings: { ...prev.fringeSettings, groups } };
    });
  };

  const addFringeGroup = () => {
    setFormData(prev => ({
      ...prev,
      fringeSettings: {
        ...prev.fringeSettings,
        groups: [
          ...(prev.fringeSettings.groups || []),
          {
            presetKey: 'default',
            rateOverride: '',
            applyToBase: true,
            applyToOT: true,
            capAmount: ''
          }
        ]
      }
    }));
  };

  const removeFringeGroup = (index) => {
    setFormData(prev => {
      const groups = [...(prev.fringeSettings.groups || [])].filter((_, i) => i !== index);
      return { ...prev, fringeSettings: { ...prev.fringeSettings, groups: groups.length ? groups : defaultFringeSettings.groups } };
    });
  };

  const handleSubcategoryChange = (value) => {
    const info = subcategories[value];
    setFormData(prev => ({
      ...prev,
      subcategory: value,
      position: '', // Reset position when subcategory changes
      accountCode: info?.account
        ? `${info.account}-${(prev.accountCode?.split('-')[1] || '01')}`
        : prev.accountCode
    }));
  };

  const handlePositionChange = (positionKey) => {
    const positionInfo = availablePositions.find(p => p.key === positionKey);
    const subcatInfo = subcategories[formData.subcategory];
    setFormData(prev => ({
      ...prev,
      position: positionKey,
      // Auto-fill description with position name if empty
      description: prev.description || positionInfo?.name || '',
      // Update account code with position suffix
      accountCode: subcatInfo?.account && positionInfo?.accountSuffix
        ? `${subcatInfo.account}-${positionInfo.accountSuffix}`
        : prev.accountCode
    }));
  };

  const handleSave = () => {
    const errors = {};
    if (!formData.accountCode) errors.accountCode = 'Account code required';
    if (!formData.description) errors.description = 'Description required';
    if (!formData.units || Number(formData.units) <= 0) errors.units = 'Units must be > 0';
    if (!formData.rate || Number(formData.rate) <= 0) errors.rate = 'Rate must be > 0';
    setFieldErrors(errors);
    if (Object.keys(errors).length) return;
    const { fringeSettings } = formData;
    onSave({
      ...formData,
      // Include _id if editing existing item
      _id: item?._id,
      fringeSettings: {
        applyFringes: fringeSettings.applyFringes,
        groups: (fringeSettings.groups || []).map((g) => ({
          presetKey: g.presetKey || 'default',
          rateOverride:
            g.rateOverride === '' || g.rateOverride === null
              ? null
              : Number(g.rateOverride),
          applyToBase: g.applyToBase !== false,
          applyToOT: g.applyToOT !== false,
          capAmount:
            g.capAmount === '' || g.capAmount === null
              ? ''
              : Number(g.capAmount)
        })),
        notes: fringeSettings.notes
      },
      // Movie Magic style fields
      etc: formData.etcOverride === '' || formData.etcOverride === null 
        ? null 
        : Number(formData.etcOverride),
      status: formData.status,
      varianceNotes: formData.varianceNotes
    });
  };

  const calculatedTotal = formData.units * formData.rate * formData.multiplier;
  const totalFringeAmount = (() => {
    if (!formData.fringeSettings.applyFringes) return 0;
    const base = formData.units * formData.rate;
    const otPortion = formData.multiplier > 1 ? base * (formData.multiplier - 1) : 0;
    const basePortion = base;
    let totalFringe = 0;
    (formData.fringeSettings.groups || []).forEach((group) => {
      const presetRate = fringePresets[group.presetKey]?.rate || 0;
      const override = group.rateOverride;
      const numericOverride = override === '' || override === null ? null : Number(override);
      if (numericOverride === 0) return;
      const effectiveRate = numericOverride !== null && !Number.isNaN(numericOverride) ? numericOverride : presetRate;
      const applicableBase = 
        (group.applyToBase ? basePortion : 0) +
        (group.applyToOT ? otPortion : 0);
      let groupAmount = applicableBase * (effectiveRate / 100);
      if (group.capAmount !== '' && group.capAmount !== null) {
        const cap = Number(group.capAmount);
        if (!Number.isNaN(cap)) {
          groupAmount = Math.min(groupAmount, cap);
        }
      }
      totalFringe += groupAmount;
    });
    return totalFringe;
  })();
  const fringeAmount = totalFringeAmount;
  const burdenedTotal = calculatedTotal + fringeAmount;

  const computeFringeForGroup = (group) => {
    if (!formData.fringeSettings.applyFringes) return 0;
    const base = formData.units * formData.rate;
    const otPortion = formData.multiplier > 1 ? base * (formData.multiplier - 1) : 0;
    const basePortion = base;
    const presetRate = fringePresets[group.presetKey]?.rate || 0;
    const override = group.rateOverride;
    const numericOverride = override === '' || override === null ? null : Number(override);
    if (numericOverride === 0) return 0;
    const effectiveRate = numericOverride !== null && !Number.isNaN(numericOverride) ? numericOverride : presetRate;
    const applicableBase = 
      (group.applyToBase ? basePortion : 0) +
      (group.applyToOT ? otPortion : 0);
    let amount = applicableBase * (effectiveRate / 100);
    if (group.capAmount !== '' && group.capAmount !== null) {
      const cap = Number(group.capAmount);
      if (!Number.isNaN(cap)) {
        amount = Math.min(amount, cap);
      }
    }
    return amount;
  };

  // Spreadsheet keyboard flow: Enter = save (except multiline/autocomplete), Esc = cancel
  const handleDialogKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
      return;
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      const isTextarea = e.target.tagName === 'TEXTAREA';
      const isAutocompleteOpen = e.target.getAttribute('aria-expanded') === 'true';
      if (!isTextarea && !isAutocompleteOpen) {
        e.preventDefault();
        handleSave();
      }
    }
  };

  // Different layout for Add vs Edit mode
  const isEditMode = !!item;

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      onKeyDown={handleDialogKeyDown}
      PaperProps={{
        sx: { 
          bgcolor: 'background.default',
          backgroundImage: 'none'
        }
      }}
    >
      {/* Movie Magic Style Header - Compact with account info */}
      <DialogTitle sx={{ pb: 1, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography 
              sx={{ 
                fontFamily: 'monospace', 
                fontSize: '1.1rem', 
                fontWeight: 700,
                bgcolor: 'action.selected',
                px: 1.5,
                py: 0.5,
                borderRadius: 1
              }}
            >
              {formData.accountCode || '____-__'}
            </Typography>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                {item ? 'Edit Detail Line' : 'New Detail Line'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {subcategories[formData.subcategory]?.label || 'Select account category'}
              </Typography>
            </Box>
          </Box>
          {/* Live Total - Always visible */}
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="caption" color="text.secondary">TOTAL</Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, fontFamily: 'monospace', color: 'primary.main' }}>
              {formatCurrency(calculatedTotal)}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Stack spacing={2}>
        {/* Movie Magic Style - Spreadsheet-like calculation row */}
        <Paper 
          variant="outlined" 
          sx={{ 
            p: 2, 
            bgcolor: alpha(theme.palette.background.paper, 0.7),
            border: 1,
            borderColor: 'divider',
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04)'
          }}
        >
          {/* Row 1: Account Selection (Add mode) or Description (Edit mode) */}
          {!isEditMode ? (
            <Grid container spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <Grid item xs={3}>
                <TextField
                  label="Account"
                  value={formData.accountCode}
                  onChange={handleChange('accountCode')}
                  fullWidth
                  size="small"
                  placeholder="3200-01"
                  required
                  error={!!fieldErrors.accountCode}
                  helperText={fieldErrors.accountCode || subcategories[formData.subcategory]?.label || 'Template account code'}
                  InputProps={{
                    sx: { fontFamily: 'monospace', fontWeight: 600 }
                  }}
                />
              </Grid>
              <Grid item xs={5}>
                <Autocomplete
                  options={accountOptions}
                  getOptionLabel={(option) => `${option.account} - ${option.label}`}
                  groupBy={(option) => option.categoryLabel}
                  value={accountOptions.find(o => o.key === formData.subcategory) || null}
                  onChange={(e, value) => value && handleAccountHelperSelect(value)}
                  size="small"
                  popupIcon={null}
                  renderInput={(params) => (
                    <TextField {...params} label="Category" placeholder="Search accounts..." />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option.key}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ fontFamily: 'monospace', fontWeight: 600, minWidth: 50 }}>
                          {option.account}
                        </Typography>
                        <Typography>{option.label}</Typography>
                      </Box>
                    </li>
                  )}
                />
              </Grid>
              <Grid item xs={4}>
                {availablePositions.length > 0 ? (
                  <TextField
                    label="Position"
                    value={formData.position}
                    onChange={(e) => handlePositionChange(e.target.value)}
                    select
                    fullWidth
                    size="small"
                  >
                    <MenuItem value=""><em>Custom</em></MenuItem>
                    {availablePositions.map((pos) => (
                      <MenuItem key={pos.key} value={pos.key}>
                        {pos.accountSuffix} - {pos.name}
                      </MenuItem>
                    ))}
                  </TextField>
                ) : (
                  <TextField
                    label="Description"
                    value={formData.description}
                    onChange={handleChange('description')}
                    fullWidth
                    size="small"
                    placeholder="Line item description"
                  />
                )}
              </Grid>
            </Grid>
          ) : null}

          {/* Description row (for Add mode with positions, or always for Edit mode) */}
          {(!isEditMode && availablePositions.length > 0) || isEditMode ? (
            <TextField
              label="Description"
              value={formData.description}
              onChange={handleChange('description')}
              fullWidth
              size="small"
              autoFocus={isEditMode}
              sx={{ mb: 2 }}
              placeholder="e.g., Director of Photography, Camera Operator"
              required
              error={!!fieldErrors.description}
              helperText={fieldErrors.description}
            />
          ) : null}

          {/* Row 2: Calculation Fields - Movie Magic spreadsheet style */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            p: 1.25,
            bgcolor: alpha(theme.palette.primary.main, 0.03),
            borderRadius: 1,
            border: 1,
            borderColor: 'divider',
            flexWrap: 'wrap'
          }}>
            <TextField
              label="Units"
              type="number"
              value={formData.units}
              onChange={handleChange('units')}
              size="small"
              sx={{ width: 80 }}
              inputProps={{ min: 0, step: 1 }}
            />
            <TextField
              value={formData.unitType}
              onChange={handleChange('unitType')}
              select
              size="small"
              sx={{ width: 90 }}
            >
              <MenuItem value="flat">Flat</MenuItem>
              <MenuItem value="day">Day</MenuItem>
              <MenuItem value="week">Week</MenuItem>
              <MenuItem value="hour">Hour</MenuItem>
              <MenuItem value="allow">Allow</MenuItem>
              <MenuItem value="each">Each</MenuItem>
              <MenuItem value="month">Month</MenuItem>
            </TextField>
            <Typography sx={{ color: 'text.secondary', fontWeight: 600 }}>×</Typography>
            <TextField
              label="Rate"
              type="number"
              value={formData.rate}
              onChange={handleChange('rate')}
              size="small"
              sx={{ width: 120 }}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>
              }}
            />
            <Typography sx={{ color: 'text.secondary', fontWeight: 600 }}>×</Typography>
            <TextField
              label="Multiplier"
              type="number"
              value={formData.multiplier}
              onChange={handleChange('multiplier')}
              size="small"
              sx={{ width: 80 }}
              inputProps={{ min: 0, step: 0.1 }}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
              <Button size="small" variant="outlined" onClick={() => applyMultiplierPreset(1)}>Straight</Button>
              <Button size="small" variant="outlined" onClick={() => applyMultiplierPreset(1.5)}>OT 1.5x</Button>
              <Button size="small" variant="outlined" onClick={() => applyMultiplierPreset(2)}>OT 2.0x</Button>
            </Box>
            <Typography sx={{ fontWeight: 700, ml: 1 }}>
              = {formatCurrency(calculatedTotal)}
            </Typography>
          </Box>
        </Paper>

        {/* Collapsible sections for advanced options */}
        <Stack spacing={2}>
          {/* Status & Tracking - compact strip (stacked above fringes) */}
          <Paper variant="outlined" sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {['estimated', 'confirmed', 'locked'].map((status) => (
                  <Chip
                    key={status}
                    icon={
                      status === 'locked'
                        ? <LockIcon sx={{ fontSize: 16 }} />
                        : status === 'confirmed'
                          ? <CheckCircleIcon sx={{ fontSize: 16 }} />
                          : <EstimatedIcon sx={{ fontSize: 16 }} />
                    }
                    label={status === 'estimated' ? 'Estimated' : status === 'confirmed' ? 'Confirmed' : 'Locked'}
                    size="small"
                    color={
                      status === 'confirmed' ? 'success' :
                      status === 'locked' ? 'warning' : 'default'
                    }
                    variant={formData.status === status ? 'filled' : 'outlined'}
                    onClick={() => setFormData(prev => ({ ...prev, status }))}
                    sx={{ textTransform: 'capitalize' }}
                  />
                ))}
              </Box>
              <TextField
                label="ETC Override"
                value={formData.etcOverride}
                onChange={handleChange('etcOverride')}
                type="number"
                size="small"
                sx={{ width: 130 }}
                placeholder="Auto"
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>
                }}
              />
            </Box>
            {isEditMode && (
              <Box sx={{ display: 'flex', gap: 2, fontSize: '0.75rem', flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Actual</Typography>
                  <Typography sx={{ fontWeight: 600 }}>{formatCurrency(item?.actualAmount || 0)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Committed</Typography>
                  <Typography sx={{ fontWeight: 600, color: 'warning.main' }}>{formatCurrency(item?.committedAmount || 0)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">ETC (calc)</Typography>
                  <Typography sx={{ fontWeight: 600, color: 'info.main' }}>
                    {formatCurrency(Math.max(0, (item?.budgetAmount || 0) - (item?.actualAmount || 0) - (item?.committedAmount || 0)))}
                  </Typography>
                </Box>
              </Box>
            )}
          </Paper>

          {/* Fringes - Multi-group (MM10 style) */}
          <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Fringes
              </Typography>
              <Switch
                checked={formData.fringeSettings.applyFringes}
                onChange={handleFringeChange('applyFringes')}
                size="small"
              />
            </Box>
            {formData.fringeSettings.applyFringes && (
              <Stack spacing={1.5}>
                {(formData.fringeSettings.groups || []).map((group, idx) => {
                  const groupAmount = computeFringeForGroup(group);
                  return (
                    <Paper key={idx} variant="outlined" sx={{ p: 1.25, borderColor: 'divider' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700 }}>
                          Group {idx + 1}
                        </Typography>
                        {idx > 0 && (
                          <Button size="small" color="error" onClick={() => removeFringeGroup(idx)}>
                            Remove
                          </Button>
                        )}
                      </Box>
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <TextField
                            label="Preset"
                            value={group.presetKey}
                            onChange={handleFringeGroupChange(idx, 'presetKey')}
                            select
                            fullWidth
                            size="small"
                          >
                            {Object.entries(fringePresets).map(([key, preset]) => (
                              <MenuItem key={key} value={key}>{preset.label}</MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                        <Grid item xs={6}>
                          <TextField
                            label="Override %"
                            value={group.rateOverride}
                            onChange={handleFringeGroupChange(idx, 'rateOverride')}
                            type="number"
                            fullWidth
                            size="small"
                            placeholder={`${fringePresets[group.presetKey]?.rate ?? 0}%`}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                checked={group.applyToBase !== false}
                                onChange={handleFringeGroupToggle(idx, 'applyToBase')}
                              />
                            }
                            label="Apply to Base"
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                checked={group.applyToOT !== false}
                                onChange={handleFringeGroupToggle(idx, 'applyToOT')}
                              />
                            }
                            label="Apply to OT"
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField
                            label="Cap Amount"
                            value={group.capAmount}
                            onChange={handleFringeGroupChange(idx, 'capAmount')}
                            type="number"
                            fullWidth
                            size="small"
                            placeholder="Optional cap"
                            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="caption" color="text.secondary">Group Fringe</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              {formatCurrency(groupAmount)}
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </Paper>
                  );
                })}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Button size="small" variant="outlined" onClick={addFringeGroup}>
                    Add Fringe Group
                  </Button>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="caption" color="text.secondary">Total Fringe</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: 'info.main' }}>
                      {formatCurrency(fringeAmount)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Burdened: {formatCurrency(burdenedTotal)}
                    </Typography>
                  </Box>
                </Box>
              </Stack>
            )}
          </Paper>
        </Stack>

        {/* Notes & Variance - Collapsible */}
        <Paper variant="outlined" sx={{ mt: 2, p: 2 }}>
          <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600 }}>
            Notes
          </Typography>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Line Notes"
                value={formData.notes}
                onChange={handleChange('notes')}
                fullWidth
                size="small"
                multiline
                rows={2}
                placeholder="Internal notes for this line item..."
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Variance Notes"
                value={formData.varianceNotes}
                onChange={handleChange('varianceNotes')}
                fullWidth
                size="small"
                multiline
                rows={2}
                placeholder="Explain over/under budget reasons..."
              />
            </Grid>
          </Grid>
        </Paper>
      </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider', justifyContent: 'space-between' }}>
        <Box>
          {item?._id && onLogExpense && (
            <Button
              startIcon={<AddIcon />}
              onClick={() => {
                onLogExpense(item);
                onClose();
              }}
              color="info"
            >
              Log Expense
            </Button>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onClose} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            {item ? 'Update' : 'Add Line'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

// Expense Dialog
function ExpenseDialog({ open, subcategories, lineItems = [], preSelectedLineItem, onClose, onSave }) {
  const [formData, setFormData] = useState({
    description: '',
    subcategory: '',
    lineItemId: '',
    amount: 0,
    expenseDate: new Date().toISOString().split('T')[0],
    paymentMethod: 'other',
    vendor: { name: '' },
    notes: ''
  });

  useEffect(() => {
    if (open) {
      // If a line item was pre-selected (from LineItemDialog), use it
      if (preSelectedLineItem) {
        setFormData({
          description: '',
          subcategory: preSelectedLineItem.subcategory || '',
          lineItemId: preSelectedLineItem._id || '',
          amount: 0,
          expenseDate: new Date().toISOString().split('T')[0],
          paymentMethod: 'other',
          vendor: { name: '' },
          notes: ''
        });
      } else {
        setFormData({
          description: '',
          subcategory: '',
          lineItemId: '',
          amount: 0,
          expenseDate: new Date().toISOString().split('T')[0],
          paymentMethod: 'other',
          vendor: { name: '' },
          notes: ''
        });
      }
    }
  }, [open, preSelectedLineItem]);

  // Filter line items by selected subcategory (only show top-level items, not sub-items)
  const handleChange = (field) => (e) => {
    if (field === 'vendorName') {
      setFormData(prev => ({ ...prev, vendor: { ...prev.vendor, name: e.target.value } }));
    } else {
      setFormData(prev => ({ ...prev, [field]: e.target.value }));
    }
  };

  const handleSave = () => {
    onSave({
      ...formData,
      amount: Number(formData.amount)
    });
  };

  // Format currency for display
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Log Expense</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {/* Account Code / Line Item Selector - Movie Magic Style */}
          <Autocomplete
            options={lineItems.filter(li => !li.parentLineItemId)}
            getOptionLabel={(option) => `${option.accountCode} - ${option.description}`}
            groupBy={(option) => subcategories[option.subcategory]?.label || option.subcategory}
            value={lineItems.find(li => li._id === formData.lineItemId) || null}
            onChange={(e, newValue) => {
              if (newValue) {
                setFormData(prev => ({
                  ...prev,
                  lineItemId: newValue._id,
                  subcategory: newValue.subcategory
                }));
              } else {
                setFormData(prev => ({ ...prev, lineItemId: '' }));
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Account Code / Line Item"
                placeholder="Search by account code or description..."
                helperText="Select a budget line item to update its actual amount"
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option._id}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                  <Typography variant="body2">
                    <strong>{option.accountCode}</strong> - {option.description}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                    Budget: {formatCurrency(option.budgetAmount)}
                  </Typography>
                </Box>
              </li>
            )}
          />

          <TextField
            label="Description"
            value={formData.description}
            onChange={handleChange('description')}
            fullWidth
            required
            placeholder="What was this expense for?"
          />

          <TextField
            label="Category"
            value={formData.subcategory}
            onChange={handleChange('subcategory')}
            select
            fullWidth
            required
            helperText={formData.lineItemId ? "Auto-filled from selected line item" : "Select a category"}
          >
            {Object.entries(subcategories).map(([key, info]) => (
              <MenuItem key={key} value={key}>
                {info.account} - {info.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Amount"
            type="number"
            value={formData.amount}
            onChange={handleChange('amount')}
            fullWidth
            required
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>
            }}
          />

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                label="Date"
                type="date"
                value={formData.expenseDate}
                onChange={handleChange('expenseDate')}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Payment Method"
                value={formData.paymentMethod}
                onChange={handleChange('paymentMethod')}
                select
                fullWidth
              >
                <MenuItem value="cash">Cash</MenuItem>
                <MenuItem value="check">Check</MenuItem>
                <MenuItem value="credit-card">Credit Card</MenuItem>
                <MenuItem value="wire">Wire Transfer</MenuItem>
                <MenuItem value="petty-cash">Petty Cash</MenuItem>
                <MenuItem value="po">Purchase Order</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </TextField>
            </Grid>
          </Grid>

          <TextField
            label="Vendor/Payee"
            value={formData.vendor.name}
            onChange={handleChange('vendorName')}
            fullWidth
            placeholder="Who was paid?"
          />

          <TextField
            label="Notes"
            value={formData.notes}
            onChange={handleChange('notes')}
            fullWidth
            multiline
            rows={2}
            placeholder="Additional details..."
          />

          {/* Show selected line item budget info */}
          {formData.lineItemId && (
            <Alert severity="info" sx={{ mt: 1 }}>
              {(() => {
                const item = lineItems.find(li => li._id === formData.lineItemId);
                if (!item) return null;
                const remaining = item.budgetAmount - (item.actualAmount || 0) - Number(formData.amount || 0);
                return (
                  <Box>
                    <Typography variant="body2">
                      <strong>Budget:</strong> {formatCurrency(item.budgetAmount)} | 
                      <strong> Spent:</strong> {formatCurrency(item.actualAmount || 0)} | 
                      <strong> This Expense:</strong> {formatCurrency(formData.amount)}
                    </Typography>
                    <Typography variant="body2" color={remaining < 0 ? 'error.main' : 'success.main'}>
                      <strong>Remaining after this expense:</strong> {formatCurrency(remaining)}
                      {remaining < 0 && ' (OVER BUDGET)'}
                    </Typography>
                  </Box>
                );
              })()}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          variant="contained" 
          onClick={handleSave}
          disabled={!formData.description || !formData.subcategory || !formData.amount}
        >
          Log Expense
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Purchase Order Dialog
function PurchaseOrderDialog({ open, subcategories, lineItems = [], onClose, onSave }) {
  const [formData, setFormData] = useState({
    vendor: { name: '', email: '', phone: '' },
    subcategory: '',
    lineItemId: '',
    items: [{ description: '', quantity: 1, unitPrice: 0 }],
    notes: ''
  });

  useEffect(() => {
    if (open) {
      setFormData({
        vendor: { name: '', email: '', phone: '' },
        subcategory: '',
        lineItemId: '',
        items: [{ description: '', quantity: 1, unitPrice: 0 }],
        notes: ''
      });
    }
  }, [open]);

  const handleVendorChange = (field) => (e) => {
    setFormData(prev => ({ 
      ...prev, 
      vendor: { ...prev.vendor, [field]: e.target.value } 
    }));
  };

  const handleItemChange = (index, field) => (e) => {
    const newItems = [...formData.items];
    newItems[index][field] = field === 'description' ? e.target.value : Number(e.target.value);
    setFormData(prev => ({ ...prev, items: newItems }));
  };

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { description: '', quantity: 1, unitPrice: 0 }]
    }));
  };

  const removeItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const total = formData.items.reduce((sum, item) => sum + (item.quantity * item.unitPrice), 0);

  const handleSave = () => {
    onSave({ ...formData, total });
  };

  // Format currency for display
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Create Purchase Order</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {/* Account Code / Line Item Selector - Movie Magic Style */}
          <Autocomplete
            options={lineItems.filter(li => !li.parentLineItemId)}
            getOptionLabel={(option) => `${option.accountCode} - ${option.description}`}
            groupBy={(option) => subcategories[option.subcategory]?.label || option.subcategory}
            value={lineItems.find(li => li._id === formData.lineItemId) || null}
            onChange={(e, newValue) => {
              if (newValue) {
                setFormData(prev => ({
                  ...prev,
                  lineItemId: newValue._id,
                  subcategory: newValue.subcategory
                }));
              } else {
                setFormData(prev => ({ ...prev, lineItemId: '' }));
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Account Code / Line Item"
                placeholder="Search by account code or description..."
                helperText="Select a budget line item to track committed amount"
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option._id}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                  <Typography variant="body2">
                    <strong>{option.accountCode}</strong> - {option.description}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                    Budget: {formatCurrency(option.budgetAmount)}
                  </Typography>
                </Box>
              </li>
            )}
          />

          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Vendor Information</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Vendor Name"
                value={formData.vendor.name}
                onChange={handleVendorChange('name')}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Category"
                value={formData.subcategory}
                onChange={(e) => setFormData(prev => ({ ...prev, subcategory: e.target.value }))}
                select
                fullWidth
                helperText={formData.lineItemId ? "Auto-filled from selected line item" : ""}
              >
                {Object.entries(subcategories).map(([key, info]) => (
                  <MenuItem key={key} value={key}>
                    {info.account} - {info.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Email"
                value={formData.vendor.email}
                onChange={handleVendorChange('email')}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Phone"
                value={formData.vendor.phone}
                onChange={handleVendorChange('phone')}
                fullWidth
              />
            </Grid>
          </Grid>

          <Divider />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Line Items</Typography>
            <Button size="small" startIcon={<AddIcon />} onClick={addItem}>
              Add Item
            </Button>
          </Box>

          {formData.items.map((item, index) => (
            <Grid container spacing={2} key={index} alignItems="center">
              <Grid item xs={5} sm={6}>
                <TextField
                  label="Description"
                  value={item.description}
                  onChange={handleItemChange(index, 'description')}
                  fullWidth
                  size="small"
                  placeholder="Item description"
                />
              </Grid>
              <Grid item xs={2.4} sm={2}>
                <TextField
                  label="Qty"
                  type="number"
                  value={item.quantity}
                  onChange={handleItemChange(index, 'quantity')}
                  fullWidth
                  size="small"
                  inputProps={{ min: 0, step: 1 }}
                />
              </Grid>
              <Grid item xs={2.4} sm={2}>
                <TextField
                  label="Unit Price"
                  type="number"
                  value={item.unitPrice}
                  onChange={handleItemChange(index, 'unitPrice')}
                  fullWidth
                  size="small"
                  InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                />
              </Grid>
              <Grid item xs={2} sm={1}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {formatCurrency(item.quantity * item.unitPrice)}
                </Typography>
              </Grid>
              <Grid item xs={0.2} sm={1}>
                {formData.items.length > 1 && (
                  <IconButton size="small" color="error" onClick={() => removeItem(index)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Grid>
            </Grid>
          ))}

          <Paper sx={{ p: 2, bgcolor: 'action.hover' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Total</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {formatCurrency(total)}
              </Typography>
            </Box>
          </Paper>

          <TextField
            label="Notes"
            value={formData.notes}
            onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
            fullWidth
            multiline
            rows={2}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave}>Create PO</Button>
      </DialogActions>
    </Dialog>
  );
}

export default Budget;
